import { createRouter, createWebHistory } from 'vue-router'
import { useHead } from '@vueuse/head'

// 路由配置
const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: {
      title: 'Ins Talk - Instagram DM Smart Assistant',
      description: 'Export, translate, and manage Instagram messages effortlessly with our smart Chrome extension.',
      keywords: 'Instagram, DM, messages, export, translate, Chrome extension',
      requiresAuth: false,
      showInNav: true
    }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('@/views/About.vue'),
    meta: {
      title: 'About Us - Ins Talk',
      description: 'Learn about Ins Talk team and our mission to simplify Instagram message management.',
      keywords: 'about us, team, mission, Instagram tools',
      requiresAuth: false,
      showInNav: true
    }
  },
  {
    path: '/features',
    name: 'Features',
    component: () => import('@/views/Features.vue'),
    meta: {
      title: 'Features - Ins Talk',
      description: 'Discover all the powerful features of Ins Talk for Instagram message management.',
      keywords: 'features, Instagram features, message export, translation',
      requiresAuth: false,
      showInNav: true
    }
  },
  {
    path: '/help',
    name: 'Help',
    component: () => import('@/views/Help.vue'),
    meta: {
      title: 'Help & Support - Ins Talk',
      description: 'Get help and support for using Ins Talk Chrome extension.',
      keywords: 'help, support, FAQ, troubleshooting',
      requiresAuth: false,
      showInNav: true
    }
  },
  {
    path: '/download',
    name: 'Download',
    component: () => import('@/views/Download.vue'),
    meta: {
      title: 'Download - Ins Talk',
      description: 'Download and install Ins Talk Chrome extension.',
      keywords: 'download, Chrome extension, installation',
      requiresAuth: false,
      showInNav: true
    }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('@/views/Privacy.vue'),
    meta: {
      title: 'Privacy Policy - Ins Talk',
      description: 'Read our privacy policy and data protection practices.',
      keywords: 'privacy policy, data protection, security',
      requiresAuth: false,
      showInNav: true
    }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('@/views/Terms.vue'),
    meta: {
      title: 'Terms of Service - Ins Talk',
      description: 'Read our terms of service and usage agreements.',
      keywords: 'terms of service, terms, legal, usage agreement',
      requiresAuth: false,
      showInNav: true
    }
  },
  // 404 页面
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: 'Page Not Found - Ins Talk',
      description: 'The page you are looking for does not exist.',
      keywords: '404, not found, error',
      requiresAuth: false,
      showInNav: false
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // 保持浏览器历史记录中的滚动位置
    if (savedPosition) {
      return savedPosition
    }
    
    // 如果是从锚点链接跳转，保持当前滚动位置
    if (from.hash) {
      return false
    }
    
    // 对于新页面，平滑滚动到顶部
    if (to.name === 'Home') {
      return { top: 0 }
    }
    
    // 其他页面保持当前滚动位置
    return false
  }
})

// 全局路由守卫
router.beforeEach(async (to, from, next) => {
  // 设置页面标题和 SEO 元数据
  if (to.meta?.title) {
    document.title = to.meta.title
    useHead({
      title: to.meta.title,
      meta: [
        { name: 'description', content: to.meta.description || '' },
        { name: 'keywords', content: to.meta.keywords || '' },
        { property: 'og:title', content: to.meta.title },
        { property: 'og:description', content: to.meta.description || '' },
        { property: 'og:type', content: 'website' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: to.meta.title },
        { name: 'twitter:description', content: to.meta.description || '' }
      ]
    })
  }
  
  // 模拟权限检查（如果需要）
  if (to.meta?.requiresAuth) {
    // 这里可以添加实际的权限检查逻辑
    console.log('Checking authentication...')
  }
  
  // 页面加载完成回调
  router.afterEach((to, from) => {
    // 页面切换完成后的逻辑
    console.log(`Navigated from ${from.name} to ${to.name}`)
    
    // 添加页面切换动画类
    document.body.classList.add('page-transitioning')
    setTimeout(() => {
      document.body.classList.remove('page-transitioning')
    }, 300)
  })
  
  next()
})

// 错误处理
router.onError((error) => {
  console.error('Router error:', error)
  // 这里可以添加错误上报逻辑
})

export default router